import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Paper,
  IconButton,
  Collapse,
  Avatar,
  Switch,
  FormControlLabel,
  Button,
  Tooltip,
} from '@mui/material';
// Using text icons since @mui/icons-material is not installed
const ExpandMoreIcon = () => <span style={{ fontSize: '12px' }}>▼</span>;
const ExpandLessIcon = () => <span style={{ fontSize: '12px' }}>▲</span>;
const InfoIcon = () => <span style={{ fontSize: '16px', display: 'inline-block', marginLeft: '4px' }}>ℹ</span>;
import { EXCHANGES } from '../../types/opportunity';
import { getExchangeLogo } from '../../utils/exchangeUrls';
import { FeeStructure } from '../../models/FeeStructure';
import type { ExchangeFeeOverride } from '../../types/settings';

interface FeeEditorProps {
  feeOverrides?: Record<string, ExchangeFeeOverride>;
  onChange: (overrides?: Record<string, ExchangeFeeOverride>) => void;
}

const FeeEditor: React.FC<FeeEditorProps> = ({ feeOverrides = {}, onChange }) => {
  const { t } = useTranslation();
  const [expandedExchanges, setExpandedExchanges] = React.useState<Set<string>>(new Set());
  const [useCustomFees, setUseCustomFees] = React.useState(!!feeOverrides && Object.keys(feeOverrides).length > 0);

  const toggleExpanded = (exchange: string) => {
    const newExpanded = new Set(expandedExchanges);
    if (newExpanded.has(exchange)) {
      newExpanded.delete(exchange);
    } else {
      newExpanded.add(exchange);
    }
    setExpandedExchanges(newExpanded);
  };

  const handleFeeChange = (
    exchange: string,
    orderType: 'maker' | 'taker',
    quote: 'USDT' | 'BTC' | 'KRW',
    value: string
  ) => {
    const numValue = value === '' ? undefined : parseFloat(value) / 100; // Convert percentage to decimal
    
    if (!useCustomFees) return;
    
    const newOverrides = { ...feeOverrides };
    
    if (!newOverrides[exchange]) {
      newOverrides[exchange] = {
        maker: {},
        taker: {}
      };
    }
    
    newOverrides[exchange][orderType][quote] = numValue;
    
    // Clean up empty entries
    if (Object.keys(newOverrides[exchange].maker).length === 0 && 
        Object.keys(newOverrides[exchange].taker).length === 0) {
      delete newOverrides[exchange];
    }
    
    onChange(Object.keys(newOverrides).length > 0 ? newOverrides : undefined);
  };

  const toggleCustomFees = (enabled: boolean) => {
    setUseCustomFees(enabled);
    if (!enabled) {
      onChange(undefined);
      setExpandedExchanges(new Set());
    }
  };

  const getFeeValue = (
    exchange: string,
    orderType: 'maker' | 'taker',
    quote: 'USDT' | 'BTC' | 'KRW'
  ): string => {
    // First check custom override
    const customFee = feeOverrides?.[exchange]?.[orderType]?.[quote];
    if (customFee !== undefined) {
      return (customFee * 100).toFixed(2);
    }
    
    // Fall back to default
    const defaultFee = FeeStructure.getFee(exchange, orderType, quote);
    return (defaultFee * 100).toFixed(2);
  };

  const isCustomized = (exchange: string): boolean => {
    return !!feeOverrides?.[exchange];
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">{t('settings.fees.tradingFeeOverrides')}</Typography>
          <Tooltip title={t('settings.fees.customizeFeesTooltip')}>
            <Box component="span" sx={{ cursor: 'help', display: 'inline-flex', alignItems: 'center' }}>
              <InfoIcon />
            </Box>
          </Tooltip>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={useCustomFees}
              onChange={(e) => toggleCustomFees(e.target.checked)}
              color="primary"
            />
          }
          label={t('settings.fees.useCustomFees')}
        />
      </Box>

      <Collapse in={useCustomFees}>
        <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
          {EXCHANGES.map((exchangeName) => {
            const isExpanded = expandedExchanges.has(exchangeName);
            const defaultFees = FeeStructure.getDefaultFees(exchangeName);
            const supportsUSDT = FeeStructure.supportsQuote(exchangeName, 'USDT');
            const supportsBTC = FeeStructure.supportsQuote(exchangeName, 'BTC');
            const supportsKRW = FeeStructure.supportsQuote(exchangeName, 'KRW');

            return (
              <Box key={exchangeName}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderBottom: isExpanded ? '1px solid' : 'none',
                    borderColor: 'divider',
                  }}
                  onClick={() => toggleExpanded(exchangeName)}
                >
                  <Avatar
                    src={getExchangeLogo(EXCHANGES.indexOf(exchangeName as any))}
                    alt={exchangeName}
                    sx={{ width: 24, height: 24, mr: 1.5 }}
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {exchangeName}
                    {isCustomized(exchangeName) && (
                      <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 1 }}>
                        ({t('settings.fees.customized')})
                      </Typography>
                    )}
                  </Typography>
                  <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={isExpanded}>
                  <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
                      {/* USDT Column */}
                      {supportsUSDT && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            {t('settings.fees.usdtMarkets')}
                          </Typography>
                          <TextField
                            size="small"
                            label={t('settings.fees.makerPercent')}
                            type="number"
                            value={getFeeValue(exchangeName, 'maker', 'USDT')}
                            onChange={(e) => handleFeeChange(exchangeName, 'maker', 'USDT', e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, max: 1, step: 0.01 }
                            }}
                            sx={{ mb: 1, width: '100%' }}
                          />
                          <TextField
                            size="small"
                            label={t('settings.fees.takerPercent')}
                            type="number"
                            value={getFeeValue(exchangeName, 'taker', 'USDT')}
                            onChange={(e) => handleFeeChange(exchangeName, 'taker', 'USDT', e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, max: 1, step: 0.01 }
                            }}
                            sx={{ width: '100%' }}
                          />
                        </Box>
                      )}

                      {/* BTC Column */}
                      {supportsBTC && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            {t('settings.fees.btcMarkets')}
                          </Typography>
                          <TextField
                            size="small"
                            label={t('settings.fees.makerPercent')}
                            type="number"
                            value={getFeeValue(exchangeName, 'maker', 'BTC')}
                            onChange={(e) => handleFeeChange(exchangeName, 'maker', 'BTC', e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, max: 1, step: 0.01 }
                            }}
                            sx={{ mb: 1, width: '100%' }}
                          />
                          <TextField
                            size="small"
                            label={t('settings.fees.takerPercent')}
                            type="number"
                            value={getFeeValue(exchangeName, 'taker', 'BTC')}
                            onChange={(e) => handleFeeChange(exchangeName, 'taker', 'BTC', e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, max: 1, step: 0.01 }
                            }}
                            sx={{ width: '100%' }}
                          />
                        </Box>
                      )}

                      {/* KRW Column */}
                      {supportsKRW && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            {t('settings.fees.krwMarkets')}
                          </Typography>
                          <TextField
                            size="small"
                            label={t('settings.fees.makerPercent')}
                            type="number"
                            value={getFeeValue(exchangeName, 'maker', 'KRW')}
                            onChange={(e) => handleFeeChange(exchangeName, 'maker', 'KRW', e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, max: 1, step: 0.01 }
                            }}
                            sx={{ mb: 1, width: '100%' }}
                          />
                          <TextField
                            size="small"
                            label={t('settings.fees.takerPercent')}
                            type="number"
                            value={getFeeValue(exchangeName, 'taker', 'KRW')}
                            onChange={(e) => handleFeeChange(exchangeName, 'taker', 'KRW', e.target.value)}
                            InputProps={{
                              inputProps: { min: 0, max: 1, step: 0.01 }
                            }}
                            sx={{ width: '100%' }}
                          />
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={() => {
                          const newOverrides = { ...feeOverrides };
                          delete newOverrides[exchangeName];
                          onChange(Object.keys(newOverrides).length > 0 ? newOverrides : undefined);
                        }}
                        disabled={!isCustomized(exchangeName)}
                      >
                        Reset to default
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Paper>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary">
            Fees are used to calculate net profit after trading costs
          </Typography>
          <Button
            size="small"
            onClick={() => {
              onChange(undefined);
              setExpandedExchanges(new Set());
            }}
          >
            Reset all to defaults
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};

export default FeeEditor;